'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';

import RestaurantMenu from './Restaurants/Menu.react';
import RestaurantLocations from './Restaurants/Locations.react';

import './Restaurants.scss';

// Query parameter that marks "the user is one level deep, on a restaurant menu".
// The enclosing Add/Swap modal is itself opened by pushing addMeal/swapMeal onto the
// history stack, so adding a second parameter here gives the restaurant menu its own
// history entry. Browser back then pops this one first and lands on the restaurant
// list, leaving the modal open, which is what the header BACK button does too.
const MENU_QUERY_PARAM = 'restaurant';

export default class Restaurants extends Component {
    static propTypes = {
        onChangeSubView: PropTypes.func,
    };

    static contextTypes = {
        confirm: PropTypes.func,
        router: PropTypes.object,
        location: PropTypes.object,
    };

    constructor(props, context) {
        super(props, context);

        this.state = {
            selectedLocation: null,
            savedLocationParams: null,
        };

        // Last value handed to onChangeSubView, so we only notify on an actual change.
        this.notifiedMenuView = false;

        // Scroll offset of the modal body, captured on the way into a menu. Only used
        // below 47em, where the results list is not its own scroll container.
        this.savedModalScrollTop = null;
    }

    componentDidMount() {
        // We always mount on the restaurant list, so a parameter left over from a previous
        // visit (modal closed from a menu, or swap type switched away and back) would be a
        // lie. Strip it with replace rather than push so no history entry is added —
        // otherwise the next selection would push an identical URL, gain no history entry,
        // and browser back would skip the list and close the modal instead.
        if (this.isUrlDriven() && this.context.location.query[MENU_QUERY_PARAM]) {
            this.setMenuQueryParam(false, true);
        }

        this.syncSubView();
    }

    componentDidUpdate() {
        this.syncSubView();
    }

    componentWillUnmount() {
        const { onChangeSubView } = this.props;

        if (onChangeSubView) {
            onChangeSubView(null);
        }
    }

    // The modal is only history-driven where it was opened by a router push (the meal
    // feed and the collection editor). The plan editor renders it from state alone, so
    // there is no history entry to hang the menu level off of; fall back to local state.
    isUrlDriven = () => {
        const { router, location } = this.context;

        return Boolean(router && location && location.query);
    };

    // Whether we're showing a restaurant menu rather than the restaurant list. When the
    // modal is history-driven the query parameter is the source of truth, so browser back
    // and header BACK resolve through exactly the same transition.
    isMenuView = () => {
        const { selectedLocation } = this.state;
        const { location } = this.context;

        if (!selectedLocation) {
            return false;
        }

        if (!this.isUrlDriven()) {
            return true;
        }

        return Boolean(location.query[MENU_QUERY_PARAM]);
    };

    setRootEl = (rootEl) => {
        this.rootEl = rootEl;
    };

    // Below 47em the results list is not its own scroll container (Locations.scss only
    // sets overflow-y above that breakpoint) — the modal body scrolls instead. That
    // element outlives the swap, but its content height collapses to the menu's, so the
    // browser clamps scrollTop and the offset is gone by the time we come back.
    //
    // Resolved from our own root rather than the document, so this works whichever of the
    // three call sites mounted the modal and reaches nothing it doesn't own.
    getModalScroller = () => (this.rootEl ? this.rootEl.closest('.el-modal1-body-container') : null);

    captureModalScroll = () => {
        const scroller = this.getModalScroller();

        this.savedModalScrollTop = scroller ? scroller.scrollTop : null;
    };

    restoreModalScroll = () => {
        if (this.savedModalScrollTop === null) {
            return;
        }

        const scroller = this.getModalScroller();

        if (scroller) {
            scroller.scrollTop = this.savedModalScrollTop;
        }

        this.savedModalScrollTop = null;
    };

    // Tell the enclosing modal whether we're one level deep (restaurant menu) so its
    // header BACK button can pop back to the restaurant list instead of closing the flow.
    syncSubView = () => {
        const { onChangeSubView } = this.props;
        const isMenuView = this.isMenuView();

        if (isMenuView === this.notifiedMenuView) {
            return;
        }

        this.notifiedMenuView = isMenuView;

        // Only on the way back out. Restoring on every render would fire on the one our own
        // setState triggers, before the URL has caught up, and clear the offset early.
        if (!isMenuView) {
            this.restoreModalScroll();
        }

        if (onChangeSubView) {
            onChangeSubView(isMenuView ? this.returnToMap : null);
        }
    };

    setMenuQueryParam = (isMenuView, useReplace = false) => {
        const { router, location } = this.context;
        const { pathname, hash } = location;

        // Copy rather than mutate the live location query, so popping back to an earlier
        // history entry cannot see a parameter that belongs to a later one.
        const query = { ...location.query };

        if (isMenuView) {
            query[MENU_QUERY_PARAM] = 1;
        } else {
            delete query[MENU_QUERY_PARAM];
        }

        if (useReplace) {
            router.replace({ pathname, query, hash });

            return;
        }

        router.push({ pathname, query, hash });
    };

    onSelectLocation = (selectedLocation, locationParams) => {
        const { confirm } = this.context;

        this.captureModalScroll();

        if (this.isUrlDriven()) {
            this.setMenuQueryParam(true);
        }

        this.setState({ selectedLocation, savedLocationParams: locationParams }, () => {
            setTimeout(() => {
                confirm(
                    <div className="nutrition-disclaimer">
                        <h2>Nutrition Disclaimer</h2>
                        <p>
                            Nutrition info on this menu is estimated. Please check ingredients before purchasing to
                            ensure any of your avoidances are not included. Menu items may change without notice and not
                            be available in all locations.
                        </p>
                        <p>
                            PLEASE NOTE: Some nutrition and allergen data are not available for restaurant foods. Your
                            nutrition profile may include parameters that are not supported in this feature due to
                            limited data availability. Please exercise care when consuming restaurant food.
                        </p>
                    </div>,
                    (accept) => false,
                    null,
                    { acceptText: 'Got it!', rejectText: null }
                );
            }, 1000);
        });
    };

    returnToMap = () => {
        // Pop the entry we pushed rather than pushing a parameter-less one. Pushing would
        // leave the menu sitting in front of us in the stack, so a browser back straight
        // after the header BACK would walk right back into it. goBack is safe here because
        // componentDidMount guarantees we only ever reach the menu via our own push.
        //
        // selectedLocation is left in place deliberately: the URL is the switch, so browser
        // forward returns to the same menu rather than to an empty one.
        if (this.isUrlDriven()) {
            this.context.router.goBack();

            return;
        }

        this.setState({ selectedLocation: null });
    };

    render() {
        const { selectedLocation, savedLocationParams } = this.state;
        const isMenuView = this.isMenuView();

        return (
            <div className="brands-db-location-search" ref={this.setRootEl}>
                {/* The list stays mounted behind the menu. Unmounting it destroys the Google
                    Map instance, losing zoom and centre, and drops the results list out of
                    the DOM along with its scroll position. */}
                <div className="restaurant-locations-holder" data-hidden={isMenuView}>
                    <RestaurantLocations
                        onSelectLocation={this.onSelectLocation}
                        savedLocationParams={savedLocationParams}
                    />
                </div>

                {isMenuView ? (
                    <RestaurantMenu selectedLocation={selectedLocation} returnToMap={this.returnToMap} />
                ) : null}
            </div>
        );
    }
}
